/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

import type { AuthorizationCodeModel, EmptyModel } from '@veeam-vspc/models/web-controllers';

import {
    TransportRequestTypes,
    TransportService,
    TransportStrategies,
    addBaseUrl,
    addParamsToQuery,
    ValidationInternalError,
    downloadFromUrl,
} from '../../core';
import { LoginSupplier } from '../../suppliers/web';
import { FileTransportConfig } from './entries';

import type { RequestErrorResponse, RequestSuccessResponse } from '../../interfaces';
import type {
    BlobFileData,
    FileTransportCommands,
    FileTransportRequestOptions,
    FileTransportServiceConfig,
    StreamFileData,
} from './interfaces';

export class FileTransportService {

    protected readonly transportService: TransportService<RequestErrorResponse | Error | Array<RequestErrorResponse | Error>>;
    protected readonly loginSupplier: LoginSupplier;
    protected readonly commands: FileTransportCommands;
    protected readonly config: FileTransportConfig;

    constructor(fileTransportServiceConfig: FileTransportServiceConfig<any, any, any>) {
        const {
            restPrefix,
            baseUrl,
            authorizationCodeProps,
            commands,
            ...transportServiceConfigParams
        } = fileTransportServiceConfig;

        this.commands = commands;
        this.config = new FileTransportConfig({
            restPrefix,
            baseUrl,
            authorizationCodeProps,
        });
        this.transportService = new TransportService<RequestErrorResponse | Error | Array<RequestErrorResponse | Error>>({
            ...transportServiceConfigParams,
            restPrefix,
            baseUrl,
            commands,
        });
        this.loginSupplier = new LoginSupplier(this.transportService);
    }

    downloadFile(fileUrl: string, { noAuth, skipBaseUrl }: Partial<FileTransportRequestOptions> = {}) {
        const authorizationCodeProps = this.config.getAuthorizationCodeProps();

        if (!authorizationCodeProps) {
            return Promise.reject(new ValidationInternalError('authorizationCodeProps.url'));
        }

        const { url, queryKey } = authorizationCodeProps;

        return (noAuth ? Promise.resolve('') : this.getDisposableAuthCode(url))
            .then((authCode: string) => {
                const resultUrl = skipBaseUrl ? fileUrl : addBaseUrl(fileUrl, this.config.getBaseUrl());

                downloadFromUrl(noAuth ? resultUrl : addParamsToQuery(resultUrl, { [queryKey]: authCode }));
            });
    }

    downloadFileAjax<T>(fileUrl: string, data?: T, { disableGlobalFilters = true, ...restOptions }: Partial<FileTransportRequestOptions> = {}) {
        return this.transportService.request<T, void | BlobFileData>(fileUrl, { ...data, disableGlobalFilters: false }, {
            strategy: TransportStrategies.WebServerDownload,
            strategyConfigs: {
                processUrlWithFiltersMiddleware: {
                    ignoreGlobalFilters: disableGlobalFilters,
                },
            },
            type: TransportRequestTypes.ToDownloadFile,
            ...restOptions,
        });
    }

    uploadFilesByStream(
        url: string,
        files: StreamFileData[],
        { disableGlobalFilters = true, ...restOptions }: Partial<FileTransportRequestOptions> = {},
    ): Promise<Array<RequestErrorResponse | Error | boolean>> {
        return this.transportService.request<StreamFileData[], Array<RequestErrorResponse | Error | boolean>>(
            url,
            files,
            {
                strategy: TransportStrategies.StreamFileUpload,
                strategyConfigs: {
                    processUrlWithFiltersMiddleware: {
                        ignoreGlobalFilters: disableGlobalFilters,
                    },
                },
                type: TransportRequestTypes.ToStreamUploadFile,
                allowedAuthAttempts: restOptions?.allowedAuthAttempts ?? 24,
                ...restOptions,
            }
        ) as Promise<Array<RequestErrorResponse | Error | boolean>>;
    }

    uploadFilesByStreamRest(
        url: string,
        files: StreamFileData[],
        { disableGlobalFilters = true, ...restOptions }: Partial<FileTransportRequestOptions> = {},
    ): Promise<Array<RequestErrorResponse | Error | boolean>> {
        return this.transportService.request<StreamFileData[], Array<RequestErrorResponse | Error | boolean>>(
            url,
            files,
            {
                strategy: TransportStrategies.StreamFileUploadRest,
                strategyConfigs: {
                    processUrlWithFiltersMiddleware: {
                        ignoreGlobalFilters: disableGlobalFilters,
                    },
                },
                type: TransportRequestTypes.ToStreamUploadFileRest,
                allowedAuthAttempts: restOptions?.allowedAuthAttempts ?? 24,
                ...restOptions,
            }
        ) as Promise<Array<RequestErrorResponse | Error | boolean>>;
    }

    uploadFormData<R = unknown>(
        url: string,
        data: FormData,
        { disableGlobalFilters = true, ...restOptions }: Partial<FileTransportRequestOptions> = {},
    ) {
        return this.transportService.request<FormData, RequestErrorResponse | Error | (RequestSuccessResponse<EmptyModel | R> | boolean)>(url, data, {
            strategy: TransportStrategies.WebServer,
            strategyConfigs: {
                processUrlWithFiltersMiddleware: {
                    ignoreGlobalFilters: disableGlobalFilters,
                },
            },
            type: TransportRequestTypes.ToUploadFormData,
            ...restOptions,
        }) as Promise<RequestErrorResponse | Error | (RequestSuccessResponse<EmptyModel | R> | boolean)>;
    }

    protected getDisposableAuthCode(url: string): Promise<string> {
        return this.loginSupplier.authorizationCode(url, { disableGlobalFilters: true }, {
            strategyConfigs: {
                processUrlWithFiltersMiddleware: {
                    ignoreGlobalFilters: true,
                },
            },
        })
            .then((resp: RequestSuccessResponse<AuthorizationCodeModel>) => resp.data?.authorizationCode ?? 'no auth code')
            .catch(() => 'auth code error');
    }

}
